import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, createRoutesFromElements, Route } from 'react-router-dom';

import ProtectedRoute from './components/ProtectedRoute';

import Login from './pages/Login';
import FacebookCallback from './pages/FacebookCallback';
import InstagramCallback from './pages/InstagramCallback';
import MailchimpCallback from './features/channel/pages/MailchimpCallback';
import RegisterStep1 from './pages/RegisterStep1';
import RegisterStep2 from './pages/RegisterStep2';
import RegisterOAuth from './pages/RegisterOAuth';
import ForgotPassword from './pages/ForgotPassword';
import ForgotPasswordThankYou from './pages/ForgotPasswordThankYou';
import ResetPassword from './pages/ResetPassword';

const BackOfficeLayout = lazy(() => import('./backOffice/components/Layout'));
const BackOfficeProtectedRoute = lazy(() => import('./backOffice/components/ProtectedRoute'));
const BackOfficeHome = lazy(() => import('./backOffice/pages/Home'));
const BackOfficeLogin = lazy(() => import('./backOffice/pages/Login'));
const BackOfficeResetPassword = lazy(() => import('./backOffice/pages/ResetPassword'));
const Firms = lazy(() => import('./backOffice/pages/firm/Firms'));
const Firm = lazy(() => import('./backOffice/pages/firm/Firm'));
const BillingMetrics = lazy(() => import('./backOffice/pages/tracking/BillingMetrics'));
const PromptTemplates = lazy(() => import('./backOffice/pages/prompt/PromptTemplates'));
const PromptTemplate = lazy(() => import('./backOffice/pages/prompt/PromptTemplate'));
const SystemPrompts = lazy(() => import('./backOffice/pages/prompt/SystemPrompts'));
const AgentPrompts = lazy(() => import('./backOffice/pages/prompt/AgentPrompts'));
const InstructionBlocks = lazy(() => import('./backOffice/pages/instructionBlock/InstructionBlocks'));
const InstructionBlock = lazy(() => import('./backOffice/pages/instructionBlock/InstructionBlock'));
const ResolvedPrompts = lazy(() => import('./backOffice/pages/instructionBlock/ResolvedPrompts'));
const BackOfficeContentTypes = lazy(() => import('./backOffice/pages/ContentTypes'));
const ContentValidators = lazy(() => import('./backOffice/pages/validator/ContentValidators'));
const ContentValidatorEdit = lazy(() => import('./backOffice/pages/validator/ContentValidatorEdit'));
const SupportRequests = lazy(() => import('./backOffice/pages/support/SupportRequests'));
const SupportRequest = lazy(() => import('./backOffice/pages/support/SupportRequest'));
const BackofficeUsers = lazy(() => import('./backOffice/pages/user/BackofficeUsers'));
const BackofficeUser = lazy(() => import('./backOffice/pages/user/BackofficeUser'));
const BackOfficeSettings = lazy(() => import('./backOffice/pages/Settings'));
const BackOfficeChangePassword = lazy(() => import('./backOffice/pages/ChangePassword'));
const FirmUser = lazy(() => import('./backOffice/pages/firm/FirmUser'));
const SystemSettings = lazy(() => import('./backOffice/pages/systemsettings/SystemSettings'));

import { appRoutes } from './app/routes';
import LegacyV2Redirect from './app/LegacyV2Redirect';
import APP_ROUTES from './app/routePaths';

import * as constants from './constants';

import App from './app/App';
import ErrorBoundary from './components/ErrorBoundary';

const backOffice = (element) => <Suspense fallback={null}>{element}</Suspense>;

const renderRouteObjects = (routes, prefix = 'route') => (
  routes.map((route, index) => {
    const routeKey = route.path ?? `${prefix}-${index}`;
    return (
      <Route key={routeKey} path={route.path} element={route.element}>
        {route.children ? renderRouteObjects(route.children, routeKey) : null}
      </Route>
    );
  })
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<ErrorBoundary><App /></ErrorBoundary>}>
      {}
      <Route path={constants.ROUTE_BACK_OFFICE_LOGIN} element={backOffice(<BackOfficeLogin />)} />
      <Route path={constants.ROUTE_BACK_OFFICE_RESET_PASSWORD} element={backOffice(<BackOfficeResetPassword />)} />
      <Route element={backOffice(<BackOfficeProtectedRoute permissions={null} />)}>
        <Route element={backOffice(<BackOfficeLayout />)}>
          <Route path={constants.ROUTE_FIRMS} element={backOffice(<Firms />)} />
          <Route path={constants.ROUTE_FIRM_EDIT} element={backOffice(<Firm />)} />
          <Route path={constants.ROUTE_FIRM_USER_EDIT} element={backOffice(<FirmUser />)} />
          <Route path={constants.ROUTE_BILLING_METRICS} element={backOffice(<BillingMetrics />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_HOME} element={backOffice(<BackOfficeHome />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_USERS} element={backOffice(<BackofficeUsers />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_USER} element={backOffice(<BackofficeUser />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATES} element={backOffice(<PromptTemplates />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_PROMPT_TEMPLATE} element={backOffice(<PromptTemplate />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_SYSTEM_PROMPTS} element={backOffice(<SystemPrompts />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS} element={backOffice(<InstructionBlocks />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_INSTRUCTION_BLOCK} element={backOffice(<InstructionBlock />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_RESOLVED_PROMPTS} element={backOffice(<ResolvedPrompts />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_TYPES} element={backOffice(<BackOfficeContentTypes />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATORS} element={backOffice(<ContentValidators />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_CONTENT_VALIDATOR} element={backOffice(<ContentValidatorEdit />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_AGENT_PROMPTS} element={backOffice(<AgentPrompts />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_SUPPORT_REQUESTS} element={backOffice(<SupportRequests />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_SUPPORT_REQUEST} element={backOffice(<SupportRequest />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_SETTINGS} element={backOffice(<BackOfficeSettings />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_CHANGE_PASSWORD} element={backOffice(<BackOfficeChangePassword />)} />
          <Route path={constants.ROUTE_BACK_OFFICE_SYSTEM_SETTINGS} element={backOffice(<SystemSettings />)} />
        </Route>
      </Route>
      <Route path="/bo/*" element={<Navigate to={constants.ROUTE_BACK_OFFICE_HOME} replace />} />

      {}
      <Route path={constants.ROUTE_LOGIN} element={<Login />} />
      <Route path={constants.ROUTE_REGISTER_USER} element={<RegisterStep1 />} />
      <Route path={constants.ROUTE_REGISTER_FIRM} element={<RegisterStep2 />} />
      <Route path={constants.ROUTE_REGISTER_FIRM_OAUTH} element={<RegisterOAuth />} />

      <Route path={constants.ROUTE_FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={constants.ROUTE_FORGOT_PASSWORD_THANK_YOU} element={<ForgotPasswordThankYou />} />
      <Route path={constants.ROUTE_RESET_PASSWORD} element={<ResetPassword />} />

      <Route path={constants.ROUTE_FACEBOOK_CALLBACK} element={<FacebookCallback />} />
      <Route path={constants.ROUTE_INSTAGRAM_CALLBACK} element={<InstagramCallback />} />
      <Route path={constants.ROUTE_MAILCHIMP_CALLBACK} element={<MailchimpCallback />} />

      {}
      <Route element={<ProtectedRoute />}>
        <Route path={constants.ROUTE_HOME} element={<Navigate to={APP_ROUTES.DASHBOARD} replace />} />
        {renderRouteObjects(appRoutes, 'app')}
      </Route>

      <Route path="/v2/*" element={<LegacyV2Redirect />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);